/**
 * God-mode prefab structures — one sealed, join-ready building per settlement
 * profession family. Each layout reproduces geometry the room solver already
 * recognizes (the founding-cabin idiom): floor, three walls + door, flat roof,
 * a vacant bed + light, and the profession's station furniture, so plopping
 * one immediately satisfies the applicant rules (vacant bed + reachable
 * recognized workstation). Layouts are in a LOCAL ground frame: +y up, the
 * door on -z (facing the player who plopped it).
 */
import { PIECE_CATALOG, pieceCatalogCost } from "./pieces.js";
import type { ItemStack } from "../rpg.js";
import type { RoomFunction } from "./rooms.js";

export interface HomesteadPrefabPiece {
  id: string;
  p: [number, number, number];
  /** Yaw about local +y, radians. */
  yaw?: number;
}

export interface HomesteadPrefab {
  id: string;
  name: string;
  /** Room functions the layout must produce — asserted by tests. */
  functions: readonly RoomFunction[];
  pieces: readonly HomesteadPrefabPiece[];
}

const YAW90 = Math.PI / 2;

/** The proven 3m sealed module: floor, door wall on -z, flat roof. */
const SHELL: readonly HomesteadPrefabPiece[] = [
  { id: "hs-floor", p: [0, 0, 0] },
  { id: "hs-wall", p: [0, 0.2, 1.5] },
  { id: "hs-wall-door", p: [0, 0.2, -1.5] },
  { id: "hs-wall", p: [1.5, 0.2, 0], yaw: YAW90 },
  { id: "hs-wall", p: [-1.5, 0.2, 0], yaw: YAW90 },
  { id: "hs-roof-flat", p: [0, 3.2, 0] },
];

/** A vacant bed and light — the dwelling half of every join-ready prefab. */
const DWELLING: readonly HomesteadPrefabPiece[] = [
  { id: "hs-bed", p: [-0.8, 0.2, -0.3] },
  { id: "hs-lamp", p: [0.8, 0.2, -0.4] },
];

function prefab(id: string, name: string, functions: readonly RoomFunction[],
  furniture: readonly HomesteadPrefabPiece[], dwelling = true): HomesteadPrefab {
  return { id, name, functions, pieces: [...SHELL, ...(dwelling ? DWELLING : []), ...furniture] };
}

export const HOMESTEAD_PREFABS: readonly HomesteadPrefab[] = [
  prefab("biogenerator", "Biogenerator Cabin", ["dwelling", "farm", "storage"], [
    { id: "hs-farm-plot", p: [-0.1, 0.2, 0.3] },
    { id: "hs-shelf", p: [0.6, 0.2, 0.9] },
    { id: "hs-chest", p: [0.7, 0.2, -0.8] },
  ]),
  prefab("synthesizer", "Materia Synthesizer Cabin", ["dwelling", "harvester"], [
    { id: "hs-harvester", p: [0.5, 0.2, 0.5] },
  ]),
  prefab("reclaimer", "Reclaimer Cabin", ["dwelling", "salvage"], [
    { id: "hs-salvage-rig", p: [0.1, 0.2, 0.5] },
  ]),
  prefab("mine", "Mine Cabin", ["dwelling", "mine"], [
    { id: "hs-mining-rig", p: [0.2, 0.2, 0.4] },
  ]),
  prefab("kitchen", "Kitchen Cabin", ["dwelling", "kitchen"], [
    { id: "hs-stove", p: [-0.3, 0.2, 0.6] },
    { id: "hs-counter", p: [0.7, 0.2, 0.6] },
  ]),
  prefab("workshop", "Workshop Cabin", ["dwelling", "workshop"], [
    { id: "hs-workbench", p: [-0.3, 0.2, 0.4] },
    { id: "hs-shelf", p: [0.6, 0.2, 0.5] },
  ]),
  prefab("smithy", "Smithy Cabin", ["dwelling", "smithy"], [
    { id: "hs-forge", p: [-0.4, 0.2, 0.6] },
    { id: "hs-anvil", p: [0.5, 0.2, 0.4] },
  ]),
  prefab("exchange", "Exchange Cabin", ["dwelling", "exchange"], [
    { id: "hs-stall", p: [0, 0.2, 0.7] },
  ]),
  prefab("office", "Administration Cabin", ["dwelling", "office"], [
    { id: "hs-admin-desk", p: [-0.2, 0.2, 0.5] },
    { id: "hs-shelf", p: [0.6, 0.2, 0.9] },
  ]),
  prefab("commons", "Commons Hall", ["commons"], [
    { id: "hs-table", p: [0, 0.2, 0.3] },
    { id: "hs-stool", p: [0.9, 0.2, 0.3] },
    { id: "hs-lamp", p: [0.9, 0.2, -0.6] },
  ], false),
  prefab("dwelling", "Spare Dwelling", ["dwelling"], []),
] as const;

export function homesteadPrefabById(id: string): HomesteadPrefab | null {
  return HOMESTEAD_PREFABS.find((entry) => entry.id === id) ?? null;
}

/** Total catalog cost — the god plopper grants exactly this before placing. */
export function homesteadPrefabCost(prefab: HomesteadPrefab): ItemStack[] {
  const totals = new Map<string, number>();
  for (const piece of prefab.pieces) {
    const index = PIECE_CATALOG.findIndex((entry) => entry.id === piece.id);
    for (const stack of index < 0 ? [] : pieceCatalogCost(index) ?? []) {
      totals.set(stack.id, (totals.get(stack.id) ?? 0) + stack.n);
    }
  }
  return [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([id, n]) => ({ id, n }));
}
